import React, { useState } from 'react';
import { usePromise } from '../../../lib/react';
import { useObserver } from 'mobx-react';
import { FormField } from '../../../components/Forms/FormField';
import { TextInput } from '../../../components/Forms/Text';
import { Button, Dropzone } from '@ourliving/ourliving-ui';
import { NavLink } from 'react-router-dom';
import { style } from '../../../components/Shared/Style';
import styled from '@emotion/styled';
import { Card } from '../../../components/Layout/Card';
import { DisplayField } from '../../../components/DisplayField/DisplayField';
import { L } from '../../../lib/i18n';
import { Table } from '../../../components/Table/Table';
import { Editor } from '@tinymce/tinymce-react';
import { ContentInformationStore } from '../ContentInformationStore';
import { DatetimeWorklog } from '../../../components/DateTimeWorklog/DateStatusWorklog';
import type { onUploadSuccess, onUploadError } from '@ourliving/ourliving-ui/dist/components/DropZone/DropZone';

//#region css
const Content = styled.div({
    display: 'flex',
    flexDirection: 'row',
    padding: style.margin.l,
});

const ContentRight = styled.div({
    display: 'flex',
    flexDirection: 'column',
    width: '49%',
});

const ButtonContainer = styled.div({
    width: 'auto',
    paddingTop: style.margin.s,
    display: 'flex',
    justifyContent: 'flex-end',
});

const Titles = styled.div({
    fontSize: style.fontSize.seta,
    paddingBottom: style.margin.m,
});

const StyledNavLink = styled(NavLink)({
    textDecoration: 'none',
    color: style.colors.primary,
    lineHeight: '32px',
});

//#endregion

export function InformationDetailEditor(props: { id: string }) {
    const store = ContentInformationStore;

    const urlDoc = `/information/${props.id}/document/new`;

    const [ready] = usePromise(async () => await store.load(props.id), [props.id]);

    const [value, setValue] = useState();

    const onUpload = (param: onUploadSuccess | onUploadError) => {
        if (param.status === 'error') return;

        store.setValue(param.file, 'image');
        store.imageUrl = URL.createObjectURL(param.file);
    };

    const save = () => {
        store.save();
    };

    const handleEditorChange = (value: any) => {
        setValue(value);
        store.setValue(value, 'description');
    };

    const items =
        ready &&
        store.documents.map((it) => ({
            name: <div>{it.name}</div>,
        }));

    return useObserver(() => {
        if (!ready) return null;
        const { errors, model } = store;
        return (
            <>
                <Content>
                    <Card width="49%" background={style.colors.white} margin={`0 12px 0 0px`}>
                        <Titles>{L('Information')}</Titles>
                        <FormField title="Bild" error={errors.title}>
                            <Dropzone
                                onUpload={onUpload}
                                variant="Media"
                                remove={() => (store.imageUrl = '')}
                                url={store.imageUrl}
                            />
                        </FormField>
                        <FormField title="Title" error={errors.title}>
                            <TextInput name="title" value={model.title || ''} onChange={store.setValue} />
                        </FormField>

                        <FormField title={L('publish_start')} error={errors.title}>
                            <TextInput name="publish_start" value={model.title || ''} onChange={store.setValue} />
                        </FormField>
                        <FormField title={L('publish_end')} error={errors.title}>
                            <TextInput name="publish_end" value={model.title || ''} onChange={store.setValue} />
                        </FormField>
                        <FormField title={L('publish')}>
                            <input type="checkbox" />
                        </FormField>
                        <FormField title={L('boardmember_area')}>
                            <input type="checkbox" />
                        </FormField>
                        <FormField title="Description" error={errors.description}>
                            <Editor
                                initialValue={model.description || ''}
                                value={value}
                                apiKey={'280lwnf4vg3t97aajf1fzc9hm89s7ikxzzq26tjd7hn09aaa'}
                                outputFormat="html"
                                onEditorChange={handleEditorChange}
                                init={{ menubar: false, branding: false }}
                            />
                        </FormField>
                        <ButtonContainer>
                            <StyledNavLink to="./">
                                <Button style={{ marginRight: style.margin.m }}>{L('cancel')}</Button>
                            </StyledNavLink>
                            <Button onClick={save}>{L('save')}</Button>
                        </ButtonContainer>
                    </Card>
                    <ContentRight>
                        <Card background={style.colors.white}>
                            <DisplayField title={L('created_at')}>
                                <DatetimeWorklog date={model.created_at} />
                            </DisplayField>
                            <DisplayField title={L('published')}>{model.published_at}</DisplayField>
                            <DisplayField title={L('created_by')}>{model.created_by_id}</DisplayField>
                            <ButtonContainer>
                                <Button>{L('notify_again')}</Button>
                            </ButtonContainer>
                        </Card>
                        <Card margin="16px 0px 0px 0px" background={style.colors.white}>
                            <Titles>{L('document')}</Titles>
                            <StyledNavLink style={{ display: 'flex', justifyContent: 'flex-end' }} to={urlDoc}>
                                <Button>{L('create_new')}</Button>
                            </StyledNavLink>

                            <Table
                                columns={[{ key: 'name', header: L('name'), arrows: true, width: '1fr' }]}
                                data={items}
                            />
                        </Card>
                    </ContentRight>
                </Content>
            </>
        );
    });
}
